import { FormikProps } from 'formik';
import React, { Ref, forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormBuilder, FormFieldType } from 'src/js/component/form-builder';

import { AudioHandling, StreamState, StreamViewModel, TsPidType } from './stream-models';
import { Button, Icon } from '@hai/ui-react';
import classNames from 'classnames';
//import { FormChangeEvent } from 'src/js/util/global-type';

export interface AudioPidPairsRef {
  todo: () => void;
}

const MAX_AUDIO_PIDS = 8;

interface AudioPidPair {
  pid: number | '';
  compression?: string; // Optional codec field for audio PIDs
  language?: string; // Optional language field for audio PIDs
  type?: TsPidType;
  handling?: AudioHandling;
  addedManually?: boolean;
}

// Dev notes: if you want additional model props to update on the fly when the form is openned, search for "hasStateChanged" in stream-edit.tsx
export const AudioPidPairs = forwardRef((props: FormikProps<StreamViewModel>, ref: Ref<AudioPidPairsRef>) => {
  const { t } = useTranslation();
  const { values, setFieldValue } = props;

  const now = performance.now();
  const appliedTime = now - values.updated;
  const hideDetectedTableBecauseOfState = values.stats.state !== StreamState.ACTIVE && !(appliedTime < 5500);

  // Initialize pairs from props directly during render
  const detectedPairsFunc = () => {
    // Get pairs from existing pids
    const audioPidsDetected = values.stats.detectedPids
      ?.filter((item) => item.type === TsPidType.AUDIO)
      .map((item) => {
        const matchingSource = values.stats.sources?.find((source) => source.programId === item.pid);
        return {
          ...item,
          active: matchingSource !== undefined || false,
        };
      });
    return audioPidsDetected;
  };

  const detectedPairs = detectedPairsFunc();

  // Initialize pairs from props directly during render
  const initialPairs = () => {
    // Get pairs from existing pids
    let existingPairs: AudioPidPair[] = values.pids
      ?.filter((item) => item.type === TsPidType.AUDIO)
      .map((item) => ({
        pid: item.pid,
        handling: item.handling,
        compression: item.compression,
        language: item.language,
      }));

    if (values.pidsAutoDetectMode === 'manual') {
      // Slice in codec and language
      existingPairs = existingPairs?.map((pair) => {
        const matchingSource = values.stats.sources?.find((source) => source.programId === pair.pid);
        // console.log('matchingSource', matchingSource, pair);
        return {
          ...pair,
          compression: matchingSource?.compression || pair.compression, // Add compression if available
          language: matchingSource?.language || pair.language, // Add language if available
        };
      });
    }

    // Ensure we have at least one pair
    if (existingPairs?.length === 0 && values.pidsAutoDetectMode === 'manual') {
      existingPairs = [
        {
          pid: '' as any,
          handling: AudioHandling.DECODE,
        },
      ];
    }

    return existingPairs;
  };

  // Create state for pairs
  const [pairs, setPairs] = useState<AudioPidPair[]>(initialPairs);
  const [radioButtonMode, setRadioButtonMode] = useState<string>(values.pidsAutoDetectMode);
  const [removingPairs] = useState<Record<number, boolean>>({});
  if (values.pidsAutoDetectMode !== radioButtonMode) {
    setRadioButtonMode(values.pidsAutoDetectMode);
    setPairs(initialPairs());
    // console.log('set initial pairs', initialPairs());
  }

  // Add new pair
  /*
  const addPair = () => {
    if (pairs.length < MAX_AUDIO_PIDS) {
      const newTupple: AudioPidPair = {
        pid: '',
        handling: AudioHandling.DECODE,
        addedManually: true,
      };
      const newPairs = [...pairs, newTupple];
      setPairs(newPairs);
      updateViewModel(newPairs);
      setTimeout(() => {
        const input = document.querySelector(`input[name="audioPids_${pairs.length}"]`) as HTMLInputElement;
        if (input) {
          input.focus();
          input.select();
        }
      }, 50);
    }
  };

  // Modified remove pair function with animation
  const removePair = (index: number) => {
    if (pairs.length >= 1) {
      // console.log('removing pair', index, pairs);
      // Mark the pair as removing to trigger animation
      setRemovingPairs((prev) => ({ ...prev, [index]: true }));

      // Wait for animation to complete before removing from state
      setTimeout(() => {
        const newPairs = [...pairs];
        newPairs.splice(index, 1);
        // console.log('deleted element', deleteEl, newPairs);
        setPairs(newPairs);
        updateViewModel(newPairs);

        // Clean up removing state
        setRemovingPairs((prev) => {
          const updated = { ...prev };
          delete updated[index];
          return updated;
        });
      }, 200); // Match this duration with CSS transition
    }
  };*/

  // Handle change of PID value
  const handlePidChange = (index: number, value: number | '') => {
    // Create a copy of the pairs array
    const newPairs = [...(pairs || [])];

    // If the index is out of bounds, fill the array with empty elements
    if (index >= newPairs.length) {
      // Calculate how many empty elements we need to add
      const elementsToAdd = index - newPairs.length + 1;

      // Create and add empty elements with default values
      for (let i = 0; i < elementsToAdd; i++) {
        newPairs.push({
          pid: '',
          handling: AudioHandling.DECODE,
          addedManually: true,
        });
      }
    }

    // Now safely set the value at the specified index
    newPairs[index].pid = value;
    newPairs[index].addedManually = true;
    // get the correct compression if is in our detected pairs

    // HERE this cause to loose focus on the field
    const matchingSource = values.stats.sources?.find((source) => source.programId === value);
    if (matchingSource) {
      newPairs[index].compression = matchingSource.compression || undefined; // Add compression if available
      newPairs[index].language = matchingSource.language || undefined; // Add language if available
    } else {
      // If no matching source, reset compression and language
      newPairs[index].compression = undefined;
      newPairs[index].language = undefined;
    }

    // Update state and view model
    // console.log('handlePidChange', index, value, newPairs);

    setPairs(newPairs);
    updateViewModel(newPairs);

    // Update Formik state AND trigger validation, otherwise it validates once over two times.
    if (Number(value) > 9) {
      setFieldValue(`audioPids_${index}`, value, true);
    } else {
      setFieldValue(`audioPids_${index}`, value, false);
    }
  };

  // Handle change of handling value
  /*
  const handleHandlingChange = (index: number, e: FormChangeEvent) => {
    const newPairs = [...pairs];
    newPairs[index].handling = Number(e?.target?.value) as AudioHandling;
    setPairs(newPairs);
    updateViewModel(newPairs);
  };*/

  // Update the view model with new pairs
  const updateViewModel = (updatedPairs: AudioPidPair[]) => {
    // Update pids field in the form
    let updatedPids: AudioPidPair[] = [];
    if (values.pids) {
      updatedPids = [...values.pids.filter((item) => item.type !== TsPidType.AUDIO)];
    }

    updatedPairs.forEach((pair) => {
      updatedPids.push({
        pid: pair.pid === '' ? 0 : pair.pid,
        type: TsPidType.AUDIO,
        handling: pair.handling,
        addedManually: pair.addedManually,
        compression: pair.compression, // Add compression
        language: pair.language, // Add language
      });
    });

    setFieldValue('pids', updatedPids); // final place where form stores the manual PIDs, #tag PID_A
  };

  const lastUpdateTimeRef = useRef<number>(0); // Track last update time
  const updatePidsCompressionAndLanguage = () => {
    // Create a copy of the pairs array
    const now = performance.now();
    // Only update if 5 seconds have passed since last update
    if (now - lastUpdateTimeRef.current < 5000) {
      return; // Skip update if less than 5 seconds have passed
    }

    // Update the timestamp
    lastUpdateTimeRef.current = now;

    setPairs((prevPairs) => {
      const newPairs = prevPairs == null ? [] : [...prevPairs];

      // Update compression and language for each pair
      newPairs.forEach((pair, index) => {
        const diff = performance.now() - values.applied;
        // console.log('diff', diff, 'ms');
        const matchingSource = values.stats.sources?.find((source) => source.programId === pair.pid);
        if (matchingSource) {
          newPairs[index].compression = matchingSource.compression || undefined; // Add compression if available
          newPairs[index].language = matchingSource.language || undefined; // Add language if available
        } else {
          if (diff > 5200) {
            // If no matching source, reset compression and language
            newPairs[index].compression = undefined;
            newPairs[index].language = undefined;
          }
        }
      });
      // console.log('setPairs', newPairs);
      return newPairs;
    });
  };

  updatePidsCompressionAndLanguage();

  // tag AUDIO_PIDS_A
  const anyManualPidValudationError =
    pairs?.some((_, index) => {
      const fieldName = `audioPids_${index}`;
      return Object.keys(props.errors)?.some((key) => key === fieldName);
    }) || Object.keys(props.errors)?.some((key) => key.startsWith('audioPids_'));

  useImperativeHandle(ref, () => ({
    todo: () => {
      //
    },
  }));
  // Utility function to split array into chunks
  const chunkArray = <T extends object>(array: T[], chunkSize: number): T[][] => {
    if (!array) {
      return [];
    }

    const chunks: T[][] = [];
    for (let i = 0; i < array.length; i += chunkSize) {
      chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
  };

  const autoFillDetectedPids = (event: any) => {
    event.preventDefault();
    if (detectedPairs && detectedPairs.length > 0) {
      const newPairs: AudioPidPair[] = detectedPairs.map((pair) => ({
        pid: pair.pid,
        compression: pair.compression,
        language: pair.language,
        handling: AudioHandling.DECODE,
        addedManually: false,
      }));

      // Update individual fields for validation
      newPairs.forEach((pair, index) => {
        setFieldValue(`audioPids_${index}`, pair.pid, true);
      });

      // Update the pairs state
      setPairs(newPairs);

      // Update the complete pids field
      updateViewModel(newPairs);
    }
  };

  const clearManualPids = (event: any) => {
    event.preventDefault();
    const newPairs: AudioPidPair[] = [0, 1, 2, 3, 4, 5, 6, 7].map((_index) => ({
      pid: '',
      handling: AudioHandling.DECODE,
    }));

    // Update individual fields for validation
    newPairs.forEach((pair, index) => {
      setFieldValue(`audioPids_${index}`, pair.pid, true);
    });

    // Update the pairs state
    setPairs(newPairs);

    // Update the complete pids field
    updateViewModel(newPairs);
  };
  // console.log('pairs', pairs);
  return (
    <>
      <div className="mk-break-before grid-column-span3">
        {detectedPairs && detectedPairs.length > 0 && !hideDetectedTableBecauseOfState && (
          <>
            {detectedPairs.length > 8 && values.pidsAutoDetectMode === 'autodetect' && (
              <div className="mk-notice">{t('stream.noticeFirst8Decoded')}</div>
            )}
            {detectedPairs.length <= 8 && values.pidsAutoDetectMode === 'autodetect' && (
              <div className="mk-notice">{t('stream.noticeDetectedAudioPids')}</div>
            )}

            <div className="mk-audio-pairs-detected-container">
              {chunkArray(detectedPairs, 5).map((chunk, chunkIndex) => (
                <div key={`chunk-${chunkIndex}`} className="box mb-0 mk-audio-pairs-detected">
                  {chunk.map((pair, itemIndex) => (
                    <div
                      key={`pair-${chunkIndex}-${itemIndex}`}
                      className={classNames('d-flex align-items-center mb-1', pair.active && 'active')}
                    >
                      <span className="mr-2">
                        {pair.pid}
                        {pair.active && (
                          <span>
                            <Icon iconname="Checkmark" style={{ width: '20px', height: '20px' }} />
                          </span>
                        )}
                      </span>
                      <span>
                        {pair.compression && <span className="">{pair.compression}</span>}
                        {pair.language && <span className="ml-2">({pair.language.toLocaleUpperCase()})</span>}
                      </span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
            <br />
            <div className="spacer p5" />
          </>
        )}
      </div>

      {values.pidsAutoDetectMode === 'manual' && (
        <>
          <div className="mk-break-before grid-column-span3">
            {values.id >= 0 &&
            pairs != null &&
            (pairs.filter((p) => Number(p.pid) > 0).length > 0 || detectedPairs?.length === 0) ? (
              <Button
                key={`audio-pair-auto-fill`}
                variant="secondary"
                onClick={clearManualPids}
                style={{ marginTop: '7px', marginBottom: '24px' }}
                disabled={pairs.filter((p) => Number(p.pid) > 0).length === 0 && detectedPairs?.length === 0}
              >
                {t('general.button.clear')}
              </Button>
            ) : (
              values.id >= 0 &&
              detectedPairs &&
              !hideDetectedTableBecauseOfState &&
              detectedPairs.length > 0 && (
                <Button
                  key={`audio-pair-auto-fill`}
                  variant="secondary"
                  onClick={autoFillDetectedPids}
                  style={{ marginTop: '7px', marginBottom: '24px' }}
                >
                  {t('general.button.autoFill')}
                  {/*24px is the space below haiui form fields, 20px validation error message + 4px bottom margin */}
                </Button>
              )
            )}
            <div className="mk-notice grid-column-span3">
              {t('stream.noticeManualPids')}
              <span className={anyManualPidValudationError && 'error'}>{t('stream.noticeManualPidsPart2')}</span>
            </div>
          </div>

          <div className="mk-break-before grid-column-span3 mk-grid-4 mk-audio-pid-manual-container">
            {[0, 1, 2, 3, 4, 5, 6, 7]
              .map((index) => {
                let pair: AudioPidPair = undefined;
                if (pairs != null) {
                  pair = pairs[index];
                }
                if (!pair) {
                  pair = { pid: '', handling: AudioHandling.DECODE, addedManually: true };
                }
                if (Number(pair.pid) > 0) {
                  const detectedPid = values.stats.detectedPids?.find(
                    (item) => item.pid === pair.pid && item.type === TsPidType.AUDIO,
                  );
                  if (detectedPid) {
                    pair.compression = detectedPid.compression;
                    pair.language = detectedPid.language;
                  }
                }
                const value = pair.pid === 0 ? '' : pair.pid.toString();
                return (
                  <div
                    key={`stream-audio-pid-pair-${index}`}
                    id={`stream-audio-pid-pair-${index}`}
                    className={`audio-pid-pair-container ${removingPairs[index] ? 'removing' : ''}`}
                    style={{
                      transition: 'opacity 200ms ease-out, transform 200ms ease-out, height 200ms ease-out',
                      opacity: removingPairs[index] ? 0 : 1,
                      zIndex: MAX_AUDIO_PIDS - index,
                    }}
                  >
                    <FormBuilder
                      translationPrefixLabels="stream.fields"
                      translationPrefixEnums="stream.enums"
                      fields={[
                        {
                          type:
                            values.pidsAutoDetectMode === 'manual'
                              ? FormFieldType.INPUT_NUMBER
                              : FormFieldType.STATIC_TEXT,
                          name: `audioPids_${index}`,
                          value: value,
                          // hint: t('stream.fields.audioPidsExplain'),
                          placeholder: `PID ${index + 1}`,
                          label: /* index === 0 ? t('stream.fields.pid') : */ '',
                          breakBefore: true,
                          suffix: pair.language
                            ? `${pair.compression} (${pair.language.toLocaleUpperCase()})`
                            : pair.compression && pair.compression !== 'UNKNOWN'
                            ? `${pair.compression}`
                            : values.stats.state === StreamState.STOPPED
                            ? undefined
                            : Number(pair.pid) > 0
                            ? t('stream.audioPipdNoAudio')
                            : undefined,
                          onChange: (e: any) => handlePidChange(index, parseInt(e.target.value) || ''),
                        } /*,
                  {
                    type: FormFieldType.DROPDOWN,
                    name: `pidHandling_${index}`,
                    className: 'd-flex',
                    options: enumValues(DefaultAudioHandling).map((value) => ({
                      value: value,
                      label: t(`stream.enums.defaultAudioHandling.${value}`),
                    })),
                    label: index === 0 ? t('stream.fields.pidHandling') : '',
                    value: pair.handling,
                    enum: DefaultAudioHandling,
                    onChange: (event: FormChangeEvent) => handleHandlingChange(index, event),
                    addon: values.pidsAutoDetectMode === 'manual' && (
                      <IconButton
                        style={{
                          backgroundColor: 'transparent',
                        }}
                        className={classNames('hai-ml-2', index > 0 ? 'hai-mt-1rem-minus' : '')}
                        onClick={(_e: any) => removePair(index)}
                      >
                        <Icon iconname="Remove" className="gray-07-svg" />
                      </IconButton>
                    ),
                  },*/,
                      ]}
                    />
                  </div>
                );
              })
              .filter(Boolean)}
          </div>
        </>
      )}

      {/*airs?.length < MAX_AUDIO_PIDS && values?.pidsAutoDetectMode === 'manual' && (
        <div className="mk-break-before">
          <Button
            key={`audio-pair-add-button`}
            variant="secondary"
            onClick={addPair}
            style={{ marginTop: '7px', marginBottom: '24px' }}
            disabled={pairs.length == MAX_AUDIO_PIDS}
          >
            {t('general.add')}
          </Button>
        </div>
      )*/}
    </>
  );
});

AudioPidPairs.displayName = 'AudioPidPairs';
